import { 
  AnalyticsEventData, 
  ContactEventData, 
  BatchEventData, 
  WebhookResponse,
  CustomEventData 
} from '../types';
import { webhookConfig } from '../config/webhookConfig';

class WebhookService {
  // 분석 이벤트 전송
  async sendAnalyticsEvent(data: AnalyticsEventData): Promise<WebhookResponse> {
    const url = webhookConfig.getWebhookUrl('analytics');
    if (!url) {
      throw new Error('분석 웹훅 URL이 설정되지 않았습니다.');
    }

    const payload = {
      event: 'analytics_event',
      data: {
        event: data.event,
        data: data.data,
        userId: data.userId,
        sessionId: data.sessionId,
        timestamp: data.timestamp || new Date().toISOString(),
      },
      metadata: this.getMetadata(),
    };

    return this.sendWebhook(url, payload);
  }

  // 연락처 이벤트 전송
  async sendContactEvent(data: ContactEventData): Promise<WebhookResponse> {
    const url = webhookConfig.getWebhookUrl('contact');
    if (!url) {
      throw new Error('연락처 웹훅 URL이 설정되지 않았습니다.');
    }

    const payload = {
      event: 'contact_form_submit',
      data: {
        name: data.name,
        email: data.email,
        message: data.message,
        subject: data.subject,
        phone: data.phone,
        company: data.company,
        source: data.source || 'contact-form',
      },
      metadata: this.getMetadata(),
    };

    return this.sendWebhook(url, payload);
  }

  // 배치 이벤트 전송
  async sendBatchEvents(data: BatchEventData): Promise<WebhookResponse> {
    const url = webhookConfig.getWebhookUrl('batch');
    if (!url) {
      throw new Error('배치 웹훅 URL이 설정되지 않았습니다.');
    }

    const payload = {
      event: 'batch_events',
      data: {
        events: data.events,
        batchId: data.batchId || `batch_${Date.now()}`,
        count: data.events.length,
        source: data.source || 'webhook-service',
      },
      metadata: this.getMetadata(),
    };

    return this.sendWebhook(url, payload);
  }

  // 커스텀 이벤트 전송
  async sendCustomEvent(
    eventName: string, 
    data: any, 
    webhookType: 'contact' | 'analytics' | 'batch' = 'analytics'
  ): Promise<WebhookResponse> {
    const url = webhookConfig.getWebhookUrl(webhookType);
    if (!url) {
      throw new Error(`${webhookType} 웹훅 URL이 설정되지 않았습니다.`);
    }

    const payload = {
      event: eventName,
      data: {
        ...data,
        timestamp: data.timestamp || new Date().toISOString(),
      },
      metadata: this.getMetadata(),
    };

    return this.sendWebhook(url, payload);
  }

  // JSON 데이터 전송 (JsonInputForm용)
  async sendJsonData(
    jsonData: any,
    eventName: string,
    webhookType: 'contact' | 'analytics' | 'batch' = 'analytics'
  ): Promise<WebhookResponse> {
    const url = webhookConfig.getWebhookUrl(webhookType);
    if (!url) {
      throw new Error(`${webhookType} 웹훅 URL이 설정되지 않았습니다.`);
    }

    const payload = {
      event: eventName,
      data: jsonData,
      metadata: this.getMetadata(),
    };

    return this.sendWebhook(url, payload);
  }

  // 실제 웹훅 전송
  private async sendWebhook(url: string, payload: any): Promise<WebhookResponse> {
    const settings = webhookConfig.getSettings();
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= settings.retryAttempts; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), settings.timeout);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'User-Agent': 'MakeWebhookClient/1.0.0',
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseData = await response.json().catch(() => ({}));

        return {
          success: true,
          message: '웹훅 전송 성공',
          data: responseData,
          timestamp: new Date().toISOString(),
        };

      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        if (attempt < settings.retryAttempts) {
          await this.delay(settings.retryDelay * attempt);
        }
      }
    }

    return {
      success: false,
      message: `웹훅 전송 실패 (${settings.retryAttempts}회 시도)`,
      error: lastError?.message || '알 수 없는 오류',
      timestamp: new Date().toISOString(),
    };
  }

  // 메타데이터 생성
  private getMetadata() {
    return {
      timestamp: new Date().toISOString(),
      source: 'webhook-service',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
      url: typeof window !== 'undefined' ? window.location.href : '',
      referer: typeof document !== 'undefined' ? document.referrer : '',
    };
  }

  // 지연 함수
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // 웹훅 URL 유효성 검사
  async testWebhook(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event: 'test',
          data: { message: '웹훅 테스트' },
          metadata: this.getMetadata(),
        }),
      });

      return response.ok;
    } catch {
      return false;
    }
  }
}

// 싱글톤 인스턴스 생성
export const webhookService = new WebhookService(); 